import { createSelector } from "reselect";

import { schema } from "../schema";
import {
  defaultEmptyContractor,
  tContractorFormData,
  tContractorFromDbContractorAsSupplier,
} from "./uiSpvMainContractor.sel";

import type { TIssuerAsContractor } from "@app/types/insider";
import type { TEmitent, TDBAccount } from "@app/types/insider";
import type { AppState } from "../xstore";

function createParameterSelector(selector: any) {
  return (_: AppState, params: any) => selector(params);
}

const getClientCode = createParameterSelector(
  (params: { clientCode: string }) => params?.clientCode || ""
);

export const select_apiProf_issuers = createSelector(
  [schema.invoiceIssuer.selectTable],
  (issuers) => {
    return issuers;
  }
);
export const select_apiProf_issuersArray = createSelector(
  schema.invoiceIssuer.selectTable,
  (issuers) => {
    return Object.values(issuers).map((issuer) => ({
      CIF: issuer.CIF.trim(),
      DENUMIRE: issuer.DENUMIRE,
      ABREVIERE: issuer.ABR,
      E_ID: issuer.E_ID,
    }));
  }
);

function mapEmitentToContractor(
  emitent: TEmitent,
  account: TDBAccount,
  cifLocked: boolean
): TIssuerAsContractor {
  return {
    __type: "EMITENT",
    __messageID: -1, // Set default or generate appropriately
    __cifLocked: cifLocked,
    CffId: emitent.E_ID, //(!important)
    ClientName: emitent.DENUMIRE,
    Address: `${emitent.ADRESA_LINIA_1 || ""} ${
      emitent.ADRESA_LINIA_2 || ""
    }`.trim(), // prevent "null null" in the address
    __address__continuation: `${emitent.ADRESA_LINIA_3 || ""} ${
      emitent.ADRESA_LINIA_4 || ""
    }`.trim(), // idem
    County: emitent.COD_JUDET,
    RegistrationNumber: emitent.NRRC,
    TaxCode: emitent.CIF,
    Iban: account?.NR_CONT || "",
    BankName: account?.BANCA || "",
    Attention: emitent.INATENTIA,
    Phone: emitent.TELEFON,
    Fax: emitent.FAX,
    ClientId: 0, // Set default or derive as needed
    AmaIdIns: 0, // Set default or derive as needed
    City: emitent.ORAS,
    CountryCode: emitent.COD_TARA,
    CountyCode: emitent.COD_JUDET,
    Email: emitent.EMAIL,
    PostalCode: emitent.COD_POSTAL,
    AdditionalAddress: "",
    BuyerReference: "",
    UsingRoEfactura: 0,
    ClientCode: emitent.ABR,
    CAPITAL_SOCIAL: emitent.CAPITAL_SOCIAL || "",
    TIP_PLATA_TVA: emitent.TIP_PLATA_TVA,
    CONTMA_PRINCIPAL: account?.CONTMA_ID || -1,
  };
}

const countInvoicesByAbr = createSelector(
  schema.invoiceFiscal.selectTableAsList,
  (invoices) => {
    return invoices.reduce((acc, item) => {
      if (item.ABR) {
        acc[item.ABR] = (acc[item.ABR] || 0) + 1;
      }
      return acc;
    }, {});
  }
);

export const selctIssuerCodesAndNames = createSelector(
  schema.invoiceIssuer.selectTableAsList,
  (issuers) => {
    return issuers.map((issuer) => ({
      ID: issuer.E_ID,
      DENUMIRE: issuer?.DENUMIRE || "Licenta nealocata",
      ABREVIERE: issuer.ABR,
    }));
  }
);

export const selectSelfContractor = createSelector(
  schema.invoiceIssuer.selectTableAsList,
  schema.issuerBankAccounts.selectTableAsList,
  countInvoicesByAbr,
  schema.uiContractorsEditing.select,
  (issuer, accounts, iAbrCount, contractorsState) => {
    if (!contractorsState.emitentID) {
      return tContractorFormData(
        tContractorFromDbContractorAsSupplier(defaultEmptyContractor),
        -1,
        -1
      );
    }
    const issuerData = issuer.find((item: TEmitent) => {
      return item.E_ID === contractorsState.emitentID;
    });

    if (!issuerData?.E_ID) {
      return tContractorFormData(
        tContractorFromDbContractorAsSupplier(defaultEmptyContractor),
        -1,
        -1
      );
    }

    const account = issuerData?.CONTMA_PRINCIPAL
      ? accounts[issuerData?.CONTMA_PRINCIPAL]
      : accounts.find((item: TDBAccount) => {
          return (
            item.EID === issuerData.E_ID &&
            item.MONEDA === "RON" &&
            item.ACTIV === 1
          );
        });
    const cifLocked = iAbrCount[issuerData.ABR] > 0;

    // todo: https://github.com/VldMrgnn/jk-react-app/issues/94 automatically create a new ABR from existing PERSEED
    //
    // important the ClientCode maps to ABR. however, the source of ABR is given by MOA,
    // we are retrieving the logindata and the home of the user is encoded in LOGINCTX

    // Given that the incividual license implies an single account, we can safely set the ABR to the PERSEED.
    // In the scenario of multi-tenant licesing we have to come up with a different approach.

    return mapEmitentToContractor(issuerData, account, cifLocked);
  }
);

/* 

moa create owner email on emtitent(PERSEED)

JG 
 - create empty amaid  ( email & username & suprem )
 - create empty emitent(PERSEED) add FF_DREPT_EMITENT 
*/
